<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { ExclamationTriangleIcon, MagnifyingGlassIcon, UsersIcon } from '@heroicons/vue/24/outline'
import { debounce } from 'lodash';
import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'
import { useStore } from 'vuex';

const props = defineProps(['term', 'placeholder', 'type']);

const store = useStore();
const csrfToken = store.state.csrfToken;

const term = ref(props.term);
const loading = ref(false);
const error = ref(false);

const suggestions = ref([]);

const computedSuggestions = computed(() => {
    if (suggestions.value.data) {
        return [{ 'job': term.value }, ...suggestions.value.data.filter(suggestion => suggestion.job !== term.value)];
    } else if (term.value?.length > 1) {
        return [{ 'job': term.value }];
    }
    return [];
})

function onSelect(query) {
    term.value = query;
    submit();
}

const search = () => {

    if (!term.value) {
        suggestions.value = [];
        return;
    };

    error.value = false;
    loading.value = true;
    fetch('/api/matching/suggestion', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            type: props.type,
            term: term.value
        })
    }).then(async (response) => {
        suggestions.value = await response.json();
        loading.value = false;
        if (response.status !== 200) {
            error.value = true;
        }
    }).catch((error) => {
        console.log(error);
    });
}

let debounceSearch = debounce(search, 500);

const change = (value) => {
    term.value = value;
    if (term.value.length > 1) {
        debounceSearch();
    } else {
        suggestions.value = [];
    }
}

const submit = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('page');
    currentUrl.searchParams.set('suchbegriff', term.value);
    window.location = currentUrl.href;
}

</script>

<template>
    <form class="tw-relative tw-w-full tw-mb-16 tw-z-10" @submit.prevent="submit">

        <div class="tw-w-full tw-transform tw-rounded-xl tw-transition-all tw-absolute tw-group">
            <Combobox @update:modelValue="onSelect" v-model="term">


                <div class="tw-relative">
                    <ComboboxInput
                        class="tw-w-full tw-border tw-border-gray-300 tw-rounded-full tw-bg-white tw-px-4 tw-py-2.5 tw-text-gray-900 focus:tw-ring-0"
                        :placeholder="props.placeholder" @change="change($event.target.value)" autocomplete="off" />

                    <button
                        class="tw-h-full tw-bg-primary-500 hover:tw-bg-primary-400 tw-absolute tw-right-0 tw-top-0 tw-flex tw-items-center tw-justify-center tw-text-white tw-px-6 tw-rounded-full tw-rounded-l-none">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                            stroke="currentColor" class="tw-w-4 tw-h-4 tw-mr-2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                        </svg>
                        Suchen
                    </button>
                </div>


                <div v-if="loading"
                    class="tw-px-4 tw-py-2 tw-text-center sm:tw-px-14 group-focus-within:tw-block tw-hidden tw-shadow-2xl tw-rounded-lg tw-border tw-bg-white">
                    <svg aria-hidden="true" class="tw-w-5 tw-h-auto tw-text-gray-200 tw-animate-spin tw-fill-gray-600"
                        viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor" />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill" />
                    </svg>
                </div>

                <ComboboxOptions v-else-if="computedSuggestions.length > 0"
                    class="-tw-mb-2 tw-max-h-72 scroll-py-2 tw-overflow-y-auto tw-py-2 tw-text-sm tw-text-gray-800 tw-pl-0 group-focus-within:tw-block tw-hidden tw-shadow-2xl tw-rounded-lg tw-border tw-bg-white">
                    <ComboboxOption v-for="suggestion in computedSuggestions" :key="suggestion.id"
                        :value="suggestion.job" as="template" v-slot="{ active }">
                        <li
                            :class="['tw-cursor-default tw-select-none tw-rounded-md tw-px-4 tw-py-2', active && 'tw-bg-indigo-600 tw-text-white']">
                            {{ suggestion.job }}
                        </li>
                    </ComboboxOption>
                </ComboboxOptions>


            </Combobox>
        </div>
    </form>
</template>
